import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import dataService from "../../../helpers/dataService";
import { libelleValeurOrientation } from "../../../helpers/valeursOrientation";
import {
  ActiveFilterChips,
  DataTable,
  InlineFilters,
  ListPageHeader,
  ListSearchForm,
  Pill,
  SearchToolbar,
  TablePagination,
  formatResultCount,
  useListPage,
} from "../../common/DataList";
import {
  DELAI_RECHERCHE,
  GROUPES_FILTRES_ORGANISATIONS,
  ORGANISATIONS_DEFAULT_FILTERS,
  ORGANISATIONS_ENDPOINT,
  ORGANISATIONS_PERSIST_KEY,
  buildOrganisationsPayload,
  definitionsFiltresOrganisations,
  organisationsAtoms,
} from "./organisationListConfig";

// ============================================================
// Liste des organisations sur le kit DataList, au gabarit de la liste des
// utilisateurs : en-tête et création, barre de recherche (nom ou code) avec le
// filtre « Afficher la caf » dans la barre, tableau à colonnes composées
// (organisation + code, partenaire d'orientation, EG uniquement, délai de
// report), pagination côté serveur. Même appel à l'API qu'avant.
// ============================================================

const COUNT_LABELS = { none: "Aucune organisation", one: "organisation", many: "organisations" };

export const getOrganisationColumns = ({ supprimer }) => [
  {
    key: "organisation",
    header: "Organisation",
    className: "col-recipient",
    render: (o) => (
      <>
        <Link to={`/organizations/${o.id}`} className="recipient-name">
          {o.name}
        </Link>
        <span className="cell-secondary">
          {o.slugName}
          {libelleValeurOrientation(o.slugName) !== o.slugName ? ` · ${libelleValeurOrientation(o.slugName)}` : ""}
        </span>
      </>
    ),
  },
  {
    key: "isOrientationPartner",
    header: "Partenaire d'orientation",
    render: (o) =>
      o.isOrientationPartner ? (
        <Pill tone="ok" icon="fa-check">
          Partenaire
        </Pill>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: "isOnlyEG",
    header: "Entretiens giratoires",
    render: (o) =>
      o.isOnlyEG ? (
        <Pill tone="info" icon="fa-sync-alt">
          EG uniquement
        </Pill>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: "nbDayReport",
    header: "Délai de report d'un RDV",
    className: "col-date",
    render: (o) => (
      <span className="cell-primary">
        {o.nbDayReport} {o.nbDayReport > 1 ? "jours" : "jour"}
      </span>
    ),
  },
  {
    key: "actions",
    header: "Actions",
    headerSrOnly: true,
    className: "col-actions",
    render: (o) => (
      <>
        <Link
          to={`/organizations/${o.id}`}
          className="btn btn-outline-primary btn-icon"
          aria-label={`Modifier ${o.name}`}
          title="Modifier"
        >
          <i className="fa fa-pen" aria-hidden="true" />
        </Link>
        <button
          type="button"
          className="btn btn-ghost btn-icon ml-1"
          onClick={() => supprimer(o)}
          aria-label={`Supprimer ${o.name}`}
          title="Supprimer"
        >
          <i className="fa fa-trash" aria-hidden="true" />
        </button>
      </>
    ),
  },
];

const OrganizationList = () => {
  const definitions = useMemo(() => definitionsFiltresOrganisations(), []);
  const list = useListPage({
    searchAtom: organisationsAtoms.searchAtom,
    resultsAtom: organisationsAtoms.resultsAtom,
    defaultFilters: ORGANISATIONS_DEFAULT_FILTERS,
    endpoint: ORGANISATIONS_ENDPOINT,
    buildPayload: buildOrganisationsPayload,
    persistKey: ORGANISATIONS_PERSIST_KEY,
    definitions,
    groups: GROUPES_FILTRES_ORGANISATIONS,
  });

  // Recherche automatique : la liste se relance 800 ms après la dernière
  // modification d'un critère (comportement historique), Entrée n'attend pas.
  const soumettre = useRef(list.submit);
  soumettre.current = list.submit;
  useEffect(() => {
    if (!list.isDirty) return undefined;
    const minuterie = setTimeout(() => soumettre.current(), DELAI_RECHERCHE);
    return () => clearTimeout(minuterie);
  }, [list.filters, list.isDirty]);

  // La confirmation est posée par dataService.remove, qui affiche aussi en
  // notification le refus de l'API (organisation rattachée à des sites).
  const supprimer = (o) => {
    dataService.remove(`organizations/${o.id}`, {}, () => {
      toast.success(`L'organisation ${o.name} a bien été supprimée`);
      list.runSearch(list.search);
    });
  };

  const columns = useMemo(
    () => getOrganisationColumns({ supprimer }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.search]
  );

  return (
    <div className="container-fluid px-5 my-5 list-page organisation-list">
      <ListPageHeader
        title="Organisations"
        subtitle="Le référentiel des organismes d'orientation : CAF, France Travail, Conseil départemental, missions locales."
        action={{ to: "/organizations/create", text: "Créer une organisation", dataPriv: "crud_organizations" }}
      />

      <ListSearchForm onSubmit={list.submit} aria-label="Recherche d'organisations">
        <SearchToolbar
          id="organisation-search"
          name="terms"
          value={list.filters.terms}
          onChange={list.onFilterChange}
          placeholder="Nom ou code de l'organisation…"
          isLoading={list.isLoading}
          isDirty={false}
          inlineFilters={
            <InlineFilters
              definitions={list.principalFilters}
              filters={list.filters}
              onChange={list.onFilterChange}
              onSet={list.setFilter}
            />
          }
          inlineFiltersLayout="ligne"
          onToggleFilters={null}
        />
        <ActiveFilterChips chips={list.chips} onRemove={list.removeFilter} onClear={list.resetFilters} />
      </ListSearchForm>

      <section className="list-card" aria-label="Résultats">
        <div className="results-bar">
          <p className="results-bar__count" role="status" aria-live="polite">
            {formatResultCount(list.count, COUNT_LABELS)}
          </p>
        </div>
        <DataTable
          caption="Liste des organisations"
          columns={columns}
          rows={list.items}
          rowKey="id"
          isLoading={list.isLoading}
          hasSearched={list.hasSearched}
          error={list.error}
          emptyState={{
            icon: "fa-building",
            title: "Aucune organisation ne correspond à ces critères",
            hint: "Modifiez votre recherche ou retirez le filtre « Afficher la caf ».",
            action: list.canReset ? { label: "Réinitialiser les filtres", onClick: list.resetFilters } : null,
          }}
        />
        <TablePagination
          page={list.page}
          nbPages={list.nbPages}
          count={list.count}
          nbItemsByPage={list.nbItemsByPage}
          onChangePage={list.changePage}
          onChangeNbItemsByPage={list.changeNbItemsByPage}
          itemLabel="organisations"
        />
      </section>
    </div>
  );
};

export default OrganizationList;
